// The pre-call brief on one store: facts pulled from the store's own
// website and Google profile, each with the exact words it came from and
// a link to the page, two opener lines, and watch-outs. The rep reads it
// in twenty seconds before dialing. Nothing in it is stated without a
// quote, which is the design the research says keeps hallucination near
// zero. Built on demand and saved on the row when the column exists.
import React, { useState } from 'react';
import { AlertTriangle, Check, Copy, ExternalLink, RefreshCw, Search, Star } from 'lucide-react';
import { assist, prospectForAssist } from './salesAssist';
import { btnSmall } from './salesUi';

const FACT_LABELS = {
  owner_name: 'Owner',
  years_in_business: 'In business',
  what_they_sell: 'They sell',
  brands: 'Brands',
  locations: 'Locations',
  hours: 'Hours',
  online_store: 'Online store',
  financing: 'Financing',
  delivery: 'Delivery',
  reputation: 'Reputation',
  recent: 'Recent',
  phone_experience: 'Phone, per reviews',
};
const FIT_TONE = { strong: 'bg-emerald-50 text-emerald-800 border-emerald-200', ok: 'bg-slate-50 text-slate-700 border-slate-200', weak: 'bg-amber-50 text-amber-800 border-amber-200' };
const host = (u) => { try { return new URL(u).hostname.replace(/^www\./, ''); } catch { return u; } };
const ago = (iso) => {
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return d <= 0 ? 'today' : d === 1 ? 'yesterday' : `${d} days ago`;
};

/**
 * onSaved(row) gets the updated prospect when the brief is stored.
 * onBuilt(brief) fires whether or not it could be stored, so the run
 * card can show it right away and log the touch.
 */
export default function BriefCard({ client, storeId, prospect: p, me, briefReady, onSaved, onBuilt, notify, compact = false }) {
  const [busy, setBusy] = useState(false);
  const [local, setLocal] = useState(null); // brief when the column is not there yet
  const [open, setOpen] = useState(!compact);
  const [copied, setCopied] = useState('');
  const [reason, setReason] = useState('');
  const brief = p.brief || local;
  const at = p.brief_at || (local ? new Date().toISOString() : null);

  const build = async () => {
    if (busy) return;
    setBusy(true);
    setReason('');
    try {
      const res = await assist(client, storeId, 'brief', { prospect: prospectForAssist(p) });
      if (!res.brief) { setReason(res.reason || 'Nothing to read on this store.'); return; }
      setLocal(res.brief);
      setOpen(true);
      onBuilt?.(res.brief);
      if (briefReady) {
        const row = await onSaved?.({ brief: res.brief, brief_at: new Date().toISOString() });
        if (row) setLocal(null);
      }
    } catch (e) {
      console.error('sales: brief failed', e);
      notify(e.message || 'Could not build the brief', 'error');
    } finally {
      setBusy(false);
    }
  };

  const copy = async (key, text) => {
    try { await navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(''), 1500); } catch { /* ignore */ }
  };

  if (!brief) {
    return (
      <div className={`rounded-2xl border border-dashed border-slate-300 bg-white ${compact ? 'px-3 py-2' : 'p-4'} flex flex-wrap items-center gap-2`}>
        <Search className="w-4 h-4 text-slate-400" />
        <span className="text-sm text-slate-600 min-w-0 flex-1">{reason || (p.website ? `Read ${host(p.website.startsWith('http') ? p.website : `https://${p.website}`)} and their Google profile before you call.` : 'No website on file. The brief will use their Google profile.')}</span>
        <button type="button" onClick={build} disabled={busy} className={btnSmall}>{busy ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Reading</> : <><Search className="w-3.5 h-3.5" /> Build the brief</>}</button>
      </div>
    );
  }

  const facts = Object.entries(brief.facts || {}).filter(([, v]) => v?.value);
  const fit = FIT_TONE[brief.fit] || FIT_TONE.ok;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full px-3 sm:px-4 py-2.5 flex flex-wrap items-center gap-2 text-left hover:bg-slate-50/70">
        <Search className="w-4 h-4 text-blue-600" />
        <span className="text-sm font-bold text-slate-900">Brief</span>
        <span className={`px-2 py-0.5 rounded-full border text-[11px] font-bold ${fit}`}>{brief.fit === 'strong' ? 'Strong fit' : brief.fit === 'weak' ? 'Weak fit' : 'Fit unclear'}</span>
        {brief.google?.rating && <span className="inline-flex items-center gap-1 text-xs text-slate-600"><Star className="w-3.5 h-3.5 text-amber-500" />{brief.google.rating} ({brief.google.review_count || 0})</span>}
        {!open && brief.openers?.[0] && <span className="text-xs text-slate-500 truncate min-w-0 flex-1">{brief.openers[0].line}</span>}
        <span className="ml-auto text-[11px] text-slate-400 whitespace-nowrap">{at ? `built ${ago(at)}` : ''} · {open ? 'hide' : 'show'}</span>
      </button>
      {open && (
        <div className="px-3 sm:px-4 pb-4 border-t border-slate-100">
          {brief.fit_reason && <p className="mt-3 text-sm text-slate-700">{brief.fit_reason}</p>}
          {brief.watch_outs?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {brief.watch_outs.map((w, i) => <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-[11px] font-semibold text-amber-900"><AlertTriangle className="w-3 h-3" /> {w}</span>)}
            </div>
          )}
          {brief.openers?.length > 0 && (
            <div className="mt-3">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Say in the first ten seconds</div>
              <ul className="mt-1.5 space-y-1.5">
                {brief.openers.map((o, i) => (
                  <li key={i} className="flex items-start gap-2 rounded-lg border border-blue-100 bg-blue-50/50 px-3 py-2">
                    <span className="text-sm text-slate-900 min-w-0 flex-1">{o.line}</span>
                    {o.source && <a href={o.source} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-blue-700" title={o.source}><ExternalLink className="w-3.5 h-3.5" /></a>}
                    <button type="button" onClick={() => copy(`o${i}`, o.line)} className="text-slate-400 hover:text-slate-700" title="Copy">{copied === `o${i}` ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}</button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {facts.length > 0 ? (
            <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
              {facts.map(([k, v]) => (
                <div key={k} className="min-w-0">
                  <dt className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{FACT_LABELS[k] || k}</dt>
                  <dd className="text-sm text-slate-900">{v.value}</dd>
                  <dd className="text-[11px] text-slate-400 truncate" title={v.quote}>
                    "{v.quote}" {v.source && <a href={v.source} target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-700">{host(v.source)}</a>}
                  </dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="mt-3 text-sm text-slate-500">Their pages did not say much. Nothing is listed that was not on a page.</p>
          )}
          {brief.google?.hours?.length > 0 && (
            <details className="mt-3">
              <summary className="text-xs font-semibold text-blue-700 cursor-pointer">Hours</summary>
              <ul className="mt-1 text-xs text-slate-600">{brief.google.hours.map((h) => <li key={h}>{h}</li>)}</ul>
            </details>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-slate-400">
            <span>From {brief.sources?.length || 0} page{brief.sources?.length === 1 ? '' : 's'}: {(brief.sources || []).map(host).join(', ')}</span>
            <button type="button" onClick={build} disabled={busy} className="ml-auto inline-flex items-center gap-1 font-semibold text-slate-500 hover:text-slate-800"><RefreshCw className={`w-3 h-3 ${busy ? 'animate-spin' : ''}`} /> Rebuild</button>
          </div>
          {!briefReady && <p className="mt-2 text-[11px] text-amber-700">Not saved: run sql/sales-pipeline/05_brief.sql once and briefs stay on the store.</p>}
        </div>
      )}
    </div>
  );
}
